import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { onboardingAPI } from '../services/api';
import { useAuth } from './AuthContext';
import toast from '../utils/toast';

// Server-computed setup checklist (GET /api/onboarding). data is null while loading, for super
// admins and on any error (an older backend answers 404): every consumer then renders nothing.
const OnboardingContext = createContext({
  data: null,
  refresh: async () => {},
  setDismissed: async () => false,
  step: () => null,
});

export function OnboardingProvider({ children }) {
  const { user } = useAuth();
  const userId = user?.id;
  const isSuperAdmin = user?.role === 'super_admin';
  const [data, setData] = useState(null);
  const requestIdRef = useRef(0);

  const refresh = useCallback(async () => {
    const requestId = ++requestIdRef.current;
    try {
      const res = await onboardingAPI.get();
      if (requestId !== requestIdRef.current) return; // stale response
      setData(res.data && Array.isArray(res.data.steps) ? res.data : null);
    } catch (err) {
      if (requestId !== requestIdRef.current) return;
      setData(null); // fail soft: hide the card, pill and hints
    }
  }, []);

  const setDismissed = useCallback(async (dismissed) => {
    try {
      await onboardingAPI.dismiss(dismissed);
      await refresh();
      return true;
    } catch (err) {
      if (!err.viewAsReadOnly) toast.error('Could not update the setup checklist');
      return false;
    }
  }, [refresh]);

  useEffect(() => {
    setData(null);
    if (userId && !isSuperAdmin) {
      refresh();
    } else {
      requestIdRef.current += 1; // invalidate any in-flight request
    }
  }, [userId, isSuperAdmin, refresh]);

  const step = useCallback((id) => (data ? data.steps.find((s) => s.id === id) || null : null), [data]);

  return (
    <OnboardingContext.Provider value={{ data, refresh, setDismissed, step }}>
      {children}
    </OnboardingContext.Provider>
  );
}

export const useOnboarding = () => useContext(OnboardingContext);

// The next n steps still to do: required ones first (list order), then optional ones.
export function nextOpenSteps(data, n) {
  if (!data) return [];
  const open = data.steps.filter((s) => s.status !== 'done');
  return [...open.filter((s) => s.required), ...open.filter((s) => !s.required)].slice(0, n);
}
